import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Modal,
  ModalClose,
  ModalDialog,
  IconButton,
  Switch,
  Stack,
  Input,
  Textarea,
  Tooltip,
  Typography,
} from '@mui/joy';
import BatchPostComposer from './BatchPostComposer';
import ReactMarkdown from 'react-markdown';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PushPinIcon from '@mui/icons-material/PushPin';
import LinkIcon from '@mui/icons-material/Link';
import ShareIcon from '@mui/icons-material/Share';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import { formatTimeAgo } from '../../utils/dateUtils';
import type {
  CongregationBatchPostWithDetails,
  BatchPostReactionType,
  CongregationMember,
} from '../../types/congregation';

interface BatchPostCardProps {
  post: CongregationBatchPostWithDetails;
  currentMember: CongregationMember | null;
  canEdit: boolean;
  canPin: boolean;
  readOnly?: boolean;
  onReaction: (postId: number, reactionType: BatchPostReactionType) => Promise<void>;
  onReplyClick: (postId: number) => void;
  onEditClick?: (postId: number, content: string, isPinned?: boolean, attachments?: any[]) => Promise<void>;
  onDeleteClick?: (postId: number) => Promise<void>;
  onPinClick?: (postId: number, isPinned: boolean) => Promise<void>;
  loading?: boolean;
}

const reactionIcons: Record<BatchPostReactionType, { border: React.ReactNode; filled: React.ReactNode; label: string }> = {
  like: { border: <ThumbUpAltIcon />, filled: <ThumbUpAltIcon />, label: 'Like' },
  heart: { border: <FavoriteBorderIcon />, filled: <FavoriteIcon />, label: 'Love' },
  celebrate: { border: <EmojiEventsIcon />, filled: <EmojiEventsIcon />, label: 'Celebrate' },
  insightful: { border: <StarBorderIcon />, filled: <StarIcon />, label: 'Insightful' },
  haribol: { border: <ChatBubbleOutlineIcon />, filled: <ChatBubbleOutlineIcon />, label: 'Haribol' },
};

export default function BatchPostCard({
  post,
  currentMember,
  canEdit,
  canPin,
  readOnly = false,
  onReaction,
  onReplyClick,
  onEditClick,
  onDeleteClick,
  onPinClick,
  loading = false,
}: BatchPostCardProps) {
  const [reactionLoading, setReactionLoading] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  const reactionTypes: BatchPostReactionType[] = ['like', 'heart', 'celebrate', 'insightful', 'haribol'];
  const reactionCounts = React.useMemo(() => {
    if (!post.reactionCounts) return {};
    try {
      return typeof post.reactionCounts === 'string' 
        ? JSON.parse(post.reactionCounts) 
        : post.reactionCounts;
    } catch {
      return {};
    }
  }, [post.reactionCounts]);

  const handleReaction = async (reactionType: BatchPostReactionType) => {
    if (reactionLoading || !currentMember) return;
    
    setReactionLoading(true);
    try {
      await onReaction(post.id, reactionType);
    } finally {
      setReactionLoading(false);
    }
  };

  const handleEditSubmit = async (content: string, isPinned: boolean, attachments?: any[]) => {
    if (!onEditClick) {
      setEditOpen(false);
      return;
    }

    try {
      await onEditClick(post.id, content, isPinned, attachments);
      setEditOpen(false);
    } catch (error) {
      console.error('Failed to edit post:', error);
    }
  };

  return (
    <>
      <Card
        sx={{
          mb: { xs: 1, sm: 2, md: 2 },
          backgroundColor: post.isPinned ? 'rgba(255, 215, 0, 0.05)' : undefined,
          border: post.isPinned ? '2px dashed #FFD700' : '1px solid',
          borderColor: post.isPinned ? '#FFD700' : 'divider',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 'sm',
            borderColor: post.isPinned ? '#FFD700' : 'primary.main',
          },
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2, md: 2.5 } } }}>
          {/* Header */}
          <Stack 
            direction="row" 
            justifyContent="space-between" 
            alignItems="flex-start" 
            mb={{ xs: 1, sm: 1.5, md: 2 }}
            gap={{ xs: 0.5, sm: 1 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                level="title-sm" 
                fontWeight="bold"
                sx={{ fontSize: { xs: '13px', sm: '14px', md: '15px' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {post.authorName}
              </Typography>
              <Typography 
                level="body-xs" 
                color="neutral"
                sx={{ fontSize: { xs: '11px', sm: '12px', md: '13px' }, display: 'block', mt: 0.25 }}
              >
                {formatTimeAgo(new Date(post.createdAt))}
              </Typography>
            </Box>
            
            <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
              {post.isPinned && (
                <Tooltip title="Pinned post">
                  <PushPinIcon sx={{ fontSize: { xs: '16px', md: '18px' }, color: '#FFD700' }} />
                </Tooltip>
              )}
              
              {!readOnly && canPin && onPinClick && (
                <Tooltip title={post.isPinned ? 'Unpin' : 'Pin'}>
                  <IconButton
                    size="sm"
                    disabled={loading}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPinClick(post.id, !post.isPinned);
                    }}
                    sx={{ 
                      p: '4px',
                      fontSize: { xs: '16px', md: '18px' },
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    {post.isPinned ? <PushPinIcon /> : <PushPinIcon />}
                  </IconButton>
                </Tooltip>
              )}
              
              {!readOnly && canEdit && onEditClick && (
                <Tooltip title="Edit post">
                  <IconButton
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditOpen(true);
                    }}
                    sx={{ 
                      p: '4px',
                      fontSize: { xs: '16px', md: '18px' },
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
              
              {!readOnly && canEdit && onDeleteClick && (
                <Tooltip title="Delete post">
                  <IconButton
                    size="sm"
                    color="danger"
                    disabled={loading}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteClick(post.id);
                    }}
                    sx={{ 
                      p: '4px',
                      fontSize: { xs: '16px', md: '18px' },
                      '&:hover': { backgroundColor: 'error.lighter' },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>

          {/* Content */}
          <Box
            mb={{ xs: 1, sm: 1.5, md: 2 }}
            sx={{
              '& p': { margin: 0, marginBottom: '0.5em' },
              '& p:last-child': { marginBottom: 0 },
              '& ul, & ol': { marginTop: '0.3em', marginBottom: '0.3em', paddingLeft: { xs: 1.5, sm: 2, md: 2.5 } },
              '& li': { marginBottom: '0.2em', fontSize: { xs: '0.85rem', md: '0.875rem' } },
              '& h1, & h2, & h3, & h4, & h5, & h6': { marginTop: '0.5em', marginBottom: '0.3em' },
              '& code': { 
                backgroundColor: 'rgba(0,0,0,0.05)', 
                padding: '2px 4px', 
                borderRadius: '3px',
                fontSize: '0.9em',
              },
              '& pre': { 
                backgroundColor: 'rgba(0,0,0,0.05)', 
                padding: { xs: 1, sm: 1.5 }, 
                borderRadius: 1,
                overflow: 'auto',
                fontSize: { xs: '0.8rem', md: '0.875rem' },
              },
              '& blockquote': {
                borderLeft: '3px solid rgba(0,0,0,0.2)',
                paddingLeft: { xs: 1, sm: 1.5 },
                marginLeft: 0,
                color: 'text.secondary',
              },
              '& a': { color: 'primary.main' },
              fontSize: { xs: '0.85rem', sm: '0.875rem', md: '0.9rem' },
              wordBreak: 'break-word',
              lineHeight: 1.6,
            }}
          >
            <ReactMarkdown>{post.content}</ReactMarkdown>
          </Box>

          {/* Attachments */}
          {post.attachments && post.attachments.length > 0 && (
            <Box mb={{ xs: 1, sm: 1.5, md: 2 }}>
              {/* Images */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: { xs: 1, sm: 1.5 },
                  mb: post.attachments.filter((att) => att.attachmentType === 'image').length > 0 ? { xs: 1, sm: 1.5 } : 0,
                }}
              >
                {post.attachments
                  .filter((att) => att.attachmentType === 'image')
                  .map((att) => (
                    <Box
                      key={att.id}
                      component="img"
                      src={att.url}
                      alt={att.filename || 'Attachment'}
                      loading="lazy"
                      sx={{
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: 1,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': { 
                          opacity: 0.8,
                          boxShadow: 'sm',
                          transform: 'scale(1.02)',
                        },
                        backgroundColor: 'background.level1',
                      }}
                      onClick={() => setSelectedImage(att.url)}
                    />
                  ))}
              </Box>

              {/* File Downloads */}
              {post.attachments.filter((att) => att.attachmentType !== 'image').length > 0 && (
                <Stack spacing={0.75}>
                  {post.attachments
                    .filter((att) => att.attachmentType === 'file')
                    .map((att) => (
                      <Button
                        key={att.id}
                        component="a"
                        href={att.url}
                        download={att.filename || true}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        size="sm"
                        startDecorator={<FileDownloadIcon sx={{ fontSize: { xs: '16px', md: '18px' } }} />}
                        sx={{ 
                          textTransform: 'none',
                          justifyContent: 'flex-start',
                          fontSize: { xs: '12px', md: '13px' },
                          py: { xs: 0.75, sm: 1 },
                        }}
                      >
                        {att.filename || 'Download file'}
                      </Button>
                    ))}

                  {post.attachments
                    .filter((att) => att.attachmentType === 'link')
                    .map((att) => (
                      <Button
                        key={att.id}
                        component="a"
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        size="sm"
                        startDecorator={<LinkIcon sx={{ fontSize: { xs: '16px', md: '18px' } }} />}
                        sx={{ 
                          textTransform: 'none',
                          justifyContent: 'flex-start',
                          fontSize: { xs: '12px', md: '13px' },
                          py: { xs: 0.75, sm: 1 },
                        }}
                      >
                        {att.filename || att.url}
                      </Button>
                    ))}
                </Stack>
              )}
            </Box>
          )}

          {/* Poll */}
          {post.poll && (
            <Box 
              mb={{ xs: 1, sm: 1.5, md: 2 }}
              sx={{ 
                backgroundColor: 'rgba(0,0,0,0.03)', 
                p: { xs: 1.25, sm: 1.5, md: 1.75 },
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography 
                level="title-sm" 
                fontWeight="bold" 
                mb={{ xs: 1, sm: 1.25 }}
                sx={{ fontSize: { xs: '13px', md: '14px' } }}
              >
                📊 {post.poll.question}
              </Typography>
              <Stack spacing={{ xs: 0.5, sm: 0.75 }}>
                {post.poll.options.map((option) => (
                  <Button
                    key={option.id}
                    variant={post.poll?.hasVoted ? 'solid' : 'outlined'}
                    fullWidth
                    sx={{
                      justifyContent: 'space-between',
                      textAlign: 'left',
                      fontSize: { xs: '12px', md: '13px' },
                      textTransform: 'none',
                      p: { xs: 1, sm: 1.25 },
                    }}
                  >
                    <Typography level="body-sm" sx={{ fontSize: 'inherit' }}>
                      {option.optionText}
                    </Typography>
                    <Typography level="body-xs" color="neutral" sx={{ fontSize: '11px' }}>
                      {option.votesCount} vote{option.votesCount !== 1 ? 's' : ''}
                    </Typography>
                  </Button>
                ))}
              </Stack>
              {post.poll.expiresAt && (
                <Typography 
                  level="body-xs" 
                  color="neutral" 
                  sx={{ mt: 1, display: 'block', fontSize: '11px' }}
                >
                  Expires: {new Date(post.poll.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </Typography>
              )}
            </Box>
          )}

          {/* Reactions Bar */}
          <Stack 
            direction="row" 
            spacing={{ xs: 0.5, sm: 0.75 }}
            mb={{ xs: 1, sm: 1.5, md: 2 }}
            sx={{ 
              flexWrap: 'wrap',
              gap: { xs: '6px', sm: '8px' },
            }}
          >
            {reactionTypes.map((reactionType) => {
              const count = (reactionCounts[reactionType] as number) || 0;
              const isUserReaction = post.myReaction === reactionType;
              const icon = reactionIcons[reactionType];

              return (
                <Tooltip
                  key={reactionType}
                  title={`${reactionType.charAt(0).toUpperCase() + reactionType.slice(1)}`}
                >
                  <Button
                    size="sm"
                    variant={isUserReaction ? 'solid' : 'outlined'}
                    startDecorator={isUserReaction ? icon.filled : icon.border}
                    onClick={() => handleReaction(reactionType)}
                    disabled={reactionLoading}
                    sx={{ 
                      minWidth: count > 0 ? 'auto' : '36px',
                      height: { xs: '28px', md: '32px' },
                      fontSize: { xs: '11px', md: '12px' },
                      p: { xs: '4px 8px', md: '6px 12px' },
                    }}
                  >
                    {count > 0 && (
                      <Typography level="body-xs" sx={{ fontSize: { xs: '10px', md: '11px' }, fontWeight: 600 }}>
                        {count}
                      </Typography>
                    )}
                  </Button>
                </Tooltip>
              );
            })}
          </Stack>

          {/* Reply and Share Buttons */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            gap={{ xs: 0.5, sm: 1 }}
          >
            <Button
              variant="plain"
              color="neutral"
              size="sm"
              startDecorator={<ChatBubbleOutlineIcon />}
              onClick={() => onReplyClick(post.id)}
              sx={{ 
                fontSize: { xs: '12px', md: '13px' },
                p: { xs: '4px 8px', md: '6px 12px' },
              }}
            >
              {post.repliesCount} Reply{post.repliesCount !== 1 ? 'ies' : ''}
            </Button>
            
            <Tooltip title={copied ? '✓ Copied!' : 'Share post'}>
              <IconButton
                size="sm"
                color={copied ? 'success' : 'neutral'}
                onClick={async () => {
                  try {
                    const url = `${window.location.origin}/congregation/batches/${post.batchId}#post-${post.id}`;
                    await navigator.clipboard.writeText(url);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  } catch (error) {
                    console.error('Failed to copy:', error);
                  }
                }}
                sx={{ p: '4px' }}
              >
                <ShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Modal 
        open={editOpen} 
        onClose={() => setEditOpen(false)}
      >
        <ModalDialog 
          variant="outlined" 
          role="alertdialog"
          maxWidth="md"
          sx={{
            maxHeight: { xs: 'calc(100vh - 32px)', md: 'calc(100vh - 64px)' },
            overflow: 'auto',
          }}
        >
          <ModalClose variant="plain" />
          <Typography component="h2" id="modal-title" level="h4" textColor="inherit" fontWeight="lg">
            Edit Post
          </Typography>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <BatchPostComposer
              initialContent={post.content}
              initialPinned={post.isPinned}
              initialAttachments={post.attachments?.map(att => ({
                id: att.id.toString(),
                attachmentType: att.attachmentType as 'image' | 'file' | 'link',
                url: att.url,
                filename: att.filename || undefined,
                mimeType: att.mimeType || undefined,
              })) || []}
              allowAttachments={true}
              allowPoll={false}
              draftKey={null}
              onSubmit={async (content: any, isPinned?: boolean, attachments?: any[]) => {
                if (typeof content === 'string') {
                  await handleEditSubmit(content, isPinned ?? false, attachments);
                }
              }}
              onCancel={() => setEditOpen(false)}
              submitLabel="Save"
              isEditing={true}
            />
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Image Lightbox */}
      <Modal
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
            }
          }
        }}
      >
        <ModalDialog
          layout="center"
          variant="plain"
          sx={{
            maxWidth: '90vw',
            maxHeight: '90vh',
            bgcolor: 'transparent',
            boxShadow: 'none',
            padding: 0,
          }}
        >
          <ModalClose 
            variant="plain"
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              zIndex: 1301,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          />
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => setSelectedImage(null)}
          >
            <Box
              component="img"
              src={selectedImage || ''}
              alt="Full size"
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                boxShadow: '0 0 50px rgba(255, 255, 255, 0.1)',
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        </ModalDialog>
      </Modal>
    </>
  );
}
